import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Check } from 'lucide-react';
import { COMMUNITY } from '../lib/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter({ source = 'home', tone = 'light' }) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error('Please tick the consent box to continue.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/subscribe`, { email, consent, source });
      setDone(true);
      toast.success('We will be in touch — gently.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong.';
      toast.error(typeof msg === 'string' ? msg : 'Please try again.');
    } finally { setBusy(false); }
  };

  const bg = tone === 'sage' ? 'bg-brand-sage' : 'bg-brand-bg-soft';

  if (done) {
    return (
      <div data-testid="newsletter-success" className={`${bg} rounded-3xl p-10 md:p-12 text-center border border-brand-brown/5`}>
        <div className="mx-auto h-14 w-14 rounded-full bg-brand-bone flex items-center justify-center mb-5">
          <Check className="text-brand-brown" size={20} />
        </div>
        <h3 className="font-serif text-3xl text-brand-brown">Held in mind.</h3>
        <p className="mt-3 font-sans text-base text-brand-brown-light/90 max-w-md mx-auto">
          Thank you. We’ll write — gently, only when there is something meaningful to share.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`newsletter-form-${source}`}
      className={`${bg} rounded-3xl p-8 md:p-12 border border-brand-brown/5`}
    >
      <div className="max-w-2xl mx-auto text-center">
        <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-3">Stay gently in touch</p>
        <h3 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">
          {COMMUNITY.name}
        </h3>
        <p className="mt-4 font-sans text-base text-brand-brown-light/95 leading-relaxed">
          {COMMUNITY.intro}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
          {COMMUNITY.receives.map((item) => (
            <span key={item} className="font-sans text-xs px-3 py-1.5 rounded-full bg-brand-bone text-brand-brown-light border border-brand-brown/5">
              {item}
            </span>
          ))}
        </div>

        <div className="mt-7 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your email address"
            data-testid={`newsletter-email-${source}`}
            className="flex-1 bg-brand-bone border border-brand-brown/10 text-brand-brown px-5 py-3.5 rounded-full font-sans text-sm focus:outline-none focus:border-brand-gold"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid={`newsletter-submit-${source}`}
            className="inline-flex items-center justify-center gap-2 font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-7 py-3.5 hover:bg-brand-brown-light transition-colors duration-500 disabled:opacity-60"
          >
            {busy ? 'Sending…' : (<>Join the Community <Send size={14} /></>)}
          </button>
        </div>

        <label className="mt-5 flex items-start gap-3 cursor-pointer max-w-xl mx-auto text-left">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid={`newsletter-consent-${source}`}
            className="mt-1.5 h-4 w-4 accent-brand-brown shrink-0"
          />
          <span className="font-sans text-xs text-brand-brown-muted leading-relaxed">
            I agree to my email being stored securely for the sole purpose of receiving <em className="italic">The Nurture the Self Journal</em> and community correspondence. I may unsubscribe at any time. Information will never be shared with third parties.
          </span>
        </label>
      </div>
    </form>
  );
}
